import { push } from 'connected-react-router';
import { actions as authActions } from '@saucelabs/sl-container-authentication';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { constants as notificationConstants } from '@saucelabs/sl-module-notifications';
import { getLDClient } from '@saucelabs/sl-util-launchdarkly';
import Sentry from '@saucelabs/sl-util-sentry';
import { STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';

import {
  checkoutPreviewAction,
  checkoutPurchaseAction,
  resetCouponCode,
  saveCouponCode,
  setApplyingCoupon,
} from './actions';
import billingInfoSelector from '../billing-info/selectors';
import { actions as subscriptionsActions } from '../subscriptions';
import {
  CONCURRENCY_KEY,
  CREDITS_PER_BLOCK,
  CREDIT_BLOCKS_KEY,
  INTERVAL_KEY,
  NOTIFICATION_ID_ERROR_FORM_DATA,
  NOTIFICATION_ID_ERROR_PURCHASE,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_LIVE,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_RDC,
  PRODUCT_USAGE,
  PRODUCT_VDC,
  PROJECTS_KEY,
  PURCHASE_SUCCESS_MODAL,
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_CHECKOUT_SUCCESS,
  USAGE_EXPERIMENT_NAME,
} from '../../constants';
import { changedOrderItemsSelector, purchaseTypeSelector } from '../purchase/selectors';
import { annualBonusCredits, creditsConsideringAnnualBonus, getPlanCodesFromOrder } from '../plans/utils';
import { formatThousand } from '../../utils/numbers';
import {
  EVENTS,
  MODE_CONCURRENCY,
  MODE_USAGE,
  PRODUCT_AREA,
  PRODUCT_SUB_AREAS,
  SUBJECT,
  trackEvent,
} from '../../tracking';

const stripBillingInfo = (billingInfo) => ({
  accountInRecurlyExists: billingInfo?.accountInRecurlyExists,
  emailPresent: !!billingInfo?.email,
  isLoading: billingInfo?.isLoading,
  paymentInfo: {
    firstNamePresent: !!billingInfo?.paymentInfo?.firstName,
    lastNamePresent: !!billingInfo?.paymentInfo?.lastName,
    postalCodePresent: !!billingInfo?.paymentInfo?.postalCode,
  },
  paymentInfoCurrent: {
    firstNamePresent: !!billingInfo?.paymentInfoCurrent?.firstName,
    lastNamePresent: !!billingInfo?.paymentInfoCurrent?.lastName,
    postalCodePresent: !!billingInfo?.paymentInfoCurrent?.postalCode,
  },
  paymentMethod: {
    cardType: billingInfo?.paymentMethod?.cardType,
    paymentMethodValid: billingInfo?.paymentMethod?.isValid,
  },
  paymentMethodCurrent: {
    cardType: billingInfo?.paymentMethodCurrent?.cardType,
    paymentMethodValid: billingInfo?.paymentMethodCurrent?.isValid,
  },
  tokenPresent: !!billingInfo?.token,
});

export const fetchCheckoutPreview = (overrideValues = {}) => (dispatch, getState) => {
  const state = getState();
  const orderItems = changedOrderItemsSelector(state);
  const billingInfo = billingInfoSelector(state);
  const couponCode = state.apps.billing.checkout.get('couponCode');

  getLDClient()
    .then((ldClient) => {
      const inUsageBilling = ldClient.variation(USAGE_EXPERIMENT_NAME, false);

      trackEvent(EVENTS.CHECKOUT_PREVIEW, {
        ...(inUsageBilling ? MODE_USAGE : MODE_CONCURRENCY),
        coupon: couponCode,
        'API Testing Projects': orderItems.get(PRODUCT_API_TESTING)?.[PROJECTS_KEY],
        'Live Concurrency': orderItems.get(PRODUCT_LIVE)?.[CONCURRENCY_KEY],
        'Mobile Quality': orderItems.get(PRODUCT_MOBILE_QUALITY)?.[CONCURRENCY_KEY] > 0,
        'RDC Concurrency': orderItems.get(PRODUCT_RDC)?.[CONCURRENCY_KEY],
        'VDC Concurrency': orderItems.get(PRODUCT_VDC)?.[CONCURRENCY_KEY],
        'Usage Credit Blocks': orderItems.get(PRODUCT_USAGE)?.[CREDIT_BLOCKS_KEY],
        Product: Object.keys(orderItems.toJS()),
      });
    });

  return (
    dispatch(checkoutPreviewAction({
      coupon: couponCode,
      plans: getPlanCodesFromOrder(orderItems),
      token: billingInfo.token,
      values: {
        billing_info: billingInfo.paymentInfo,
        email: billingInfo.paymentInfo?.email,
      },
      ...overrideValues,
    }))
      .then((response) => {
        trackStandardEvent(
          STANDARD_EVENTS.SERVER_RESPONDED,
          PRODUCT_AREA,
          PRODUCT_SUB_AREAS.CHECKOUT,
          SUBJECT.PREVIEW_SUCCESS,
        );
        return response;
      })
      .catch((error) => {
        trackStandardEvent(
          STANDARD_EVENTS.SERVER_RESPONDED,
          PRODUCT_AREA,
          PRODUCT_SUB_AREAS.CHECKOUT,
          SUBJECT.PREVIEW_FAILED,
          { responseData: JSON.stringify(error?.response?.data) },
        );

        const errorDetail = error?.response?.data?.detail;
        const applyingCoupon = state.apps.billing.checkout.get('applyingCoupon');

        if (errorDetail && errorDetail[0]?.param === 'coupon_codes[0]' && !applyingCoupon) {
          dispatch(resetCouponCode(couponCode));
          dispatch(fetchCheckoutPreview({ ...overrideValues, coupon: null })).then(() => {
            dispatch(notificationActions.notify({
              category: notificationConstants.CATEGORY_WARNING,
              persist: true,
              text: 'The coupon code you entered is not valid for this purchase.',
              v2: true,
            }));
          });
        } else {
          Sentry.withScope((scope) => {
            scope.setContext('billingInfo', stripBillingInfo(billingInfo));
            scope.setContext('couponCode', couponCode);
            scope.setContext('orderItems', orderItems.toJS());
            scope.setContext('responseData', JSON.stringify(error?.response?.data));
            Sentry.captureException(new Error('Failed to fetch checkout preview'));
          });
          throw error;
        }
      })
  );
};

export const applyCoupon = (coupon) => (dispatch) => {
  if (coupon) {
    trackStandardEvent(
      STANDARD_EVENTS.OBJECT_CLICKED,
      PRODUCT_AREA,
      PRODUCT_SUB_AREAS.CHECKOUT,
      SUBJECT.APPLY_COUPON,
      { coupon },
    );
  } else {
    trackStandardEvent(
      STANDARD_EVENTS.OBJECT_CLICKED,
      PRODUCT_AREA,
      PRODUCT_SUB_AREAS.CHECKOUT,
      SUBJECT.RESET_COUPON,
    );
  }

  dispatch(setApplyingCoupon(true));
  return dispatch(fetchCheckoutPreview({ coupon }))
    .then(() => dispatch(saveCouponCode(coupon)))
    .catch((err) => {
      dispatch(resetCouponCode(coupon));
      throw err;
    })
    .finally(() => dispatch(setApplyingCoupon(false)));
};

/*
  Our API call to billing service via checkoutPurchaseAction is doing a change in Recurly
  service, if  it's successful, Recurly sends webhook to user-service to update user_type -
  We do not have a way to know exactly when user_type will be updated.
  On average, it happens after 10-20 seconds.
*/
export const refreshUserType = () => (dispatch, getState) => {
  dispatch(authActions.getMeUserService()).then(() => {
    const { user } = getState();
    if (user.isFreeUser()) {
      setTimeout(() => dispatch(refreshUserType()), 5000);
    }
  });
};

export const checkoutPurchase = ({ threeDsTokenId }) => (dispatch, getState) => {
  const state = getState();
  const billingInfoState = billingInfoSelector(state);
  const paymentInfo = billingInfoState.paymentInfoCurrent || billingInfoState.paymentInfo;
  const orderItems = changedOrderItemsSelector(getState());
  const couponCode = state.apps.billing.checkout.get('couponCode');

  getLDClient()
    .then((ldClient) => {
      const inUsageBilling = ldClient.variation(USAGE_EXPERIMENT_NAME, false);

      trackEvent(EVENTS.CHECKOUT_ATTEMPT_ORDER, {
        ...(inUsageBilling ? MODE_USAGE : MODE_CONCURRENCY),
        'API Testing Projects': orderItems.get(PRODUCT_API_TESTING)?.[PROJECTS_KEY],
        'Billing Type': purchaseTypeSelector(state),
        'Live Concurrency': orderItems.get(PRODUCT_LIVE)?.[CONCURRENCY_KEY],
        'Mobile Quality': orderItems.get(PRODUCT_MOBILE_QUALITY)?.[CONCURRENCY_KEY] > 0,
        'RDC Concurrency': orderItems.get(PRODUCT_RDC)?.[CONCURRENCY_KEY],
        'Usage Credit Blocks': orderItems.get(PRODUCT_USAGE)?.[CREDIT_BLOCKS_KEY],
        'VDC Concurrency': orderItems.get(PRODUCT_VDC)?.[CONCURRENCY_KEY],
        Product: Object.keys(orderItems.toJS()),
      });
    });

  const planCodes = getPlanCodesFromOrder(orderItems);

  return dispatch(checkoutPurchaseAction({
    coupon: couponCode,
    plans: planCodes,
    token: billingInfoState.token,
    values: {
      billing_info: paymentInfo,
      email: billingInfoState.paymentInfo?.email,
    },
    threeDsTokenId,
  }))
    .catch((error) => {
      if (!['3d_secure_action_required', 'three_d_secure_action_required'].includes(error?.response?.data?.detail?.category)) { // Ignoring 3DSecure2 verification "errors"
        Sentry.withScope((scope) => {
          scope.setContext('billingInfo', stripBillingInfo(billingInfoState));
          scope.setContext('couponCode', couponCode);
          scope.setContext('orderItems', orderItems.toJS());
          scope.setContext('responseData', JSON.stringify(error?.response?.data));
          scope.setContext('threeDsTokenId', threeDsTokenId);
          Sentry.captureException(new Error('Failed to make a purchase'));
        });
      }
      throw error;
    })
    .then((response) => {
      dispatch(subscriptionsActions.resetSubscriptionsReducer());
      dispatch(refreshUserType());
      return { orderItems, paymentInfo, response };
    });
};

export const checkoutError = (payload) => (dispatch) => (
  dispatch(notificationActions.notify({
    id: NOTIFICATION_ID_ERROR_PURCHASE,
    text: payload,
    persist: true,
    category: notificationConstants.CATEGORY_DANGER,
  }))
);

export const clearNotifications = () => (dispatch) => {
  dispatch(notificationActions.hideNotification(NOTIFICATION_ID_ERROR_PURCHASE));
  dispatch(notificationActions.hideNotification(NOTIFICATION_ID_ERROR_FORM_DATA));
};

export const handleCheckoutSuccess = ({
  activeSubscriptions,
  isUserFree,
  orderItems,
  paymentInfo,
} = {}) => (dispatch) => {
  const creditBlocks = orderItems.get(PRODUCT_USAGE)?.[CREDIT_BLOCKS_KEY];

  if (creditBlocks > 0) {
    const existingUsageSubscription = activeSubscriptions.find((sub) => sub.plan.isUsage());
    const purchasedInterval = orderItems.get(PRODUCT_USAGE)?.[INTERVAL_KEY];
    const purchasedCredits = purchasedInterval === PLAN_INTERVAL.YEAR ?
      creditBlocks * CREDITS_PER_BLOCK + annualBonusCredits(creditBlocks) :
      creditBlocks * CREDITS_PER_BLOCK;

    if (orderItems.size === 1 && !existingUsageSubscription) {
      // When buying a single credits plan (and it's not an upgrade), redirect to the success page
      dispatch(push(`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT_SUCCESS}`, { email: paymentInfo.email, credits: purchasedCredits }));
    } else {
      // When buying a new credits plan along with something else, or when upgrading a usage plan,
      // take the user directly to active subscriptions
      dispatch(push(`/${ROUTE_PARENT_SECTION}`));
    }
    const notificationContent = {
      header: 'Thank you for your purchase',
      text: 'We’re getting everything ready for you. This could take up to 5 minutes.',
    };
    if (existingUsageSubscription) {
      if (existingUsageSubscription.getInterval() === purchasedInterval) {
        notificationContent.header = `You successfully updated your plan to ${formatThousand(purchasedCredits, 'standard')} credits/${purchasedInterval}.`;
        const credits = creditsConsideringAnnualBonus(
          (creditBlocks - existingUsageSubscription.getCreditBlocksAmount()) * CREDITS_PER_BLOCK,
          purchasedInterval === PLAN_INTERVAL.YEAR,
        );
        notificationContent.text = `You received an additional ${formatThousand(credits, 'standard')} credits today. Your new plan will take effect on your next billing day.`;
      } else {
        notificationContent.header = `You successfully updated your plan to ${formatThousand(purchasedCredits, 'standard')} credits/${purchasedInterval}.`;
        notificationContent.text = null;
      }
    }
    dispatch(notificationActions.notify({
      category: 'success',
      persist: false,
      time: 10000,
      v2: true,
      ...notificationContent,
    }));
  } else {
    dispatch(push(`/${ROUTE_PARENT_SECTION}`));
    dispatch(notificationActions.notifySuccess('You\'ve successfully updated your subscription.', '', false, () => null, true));

    if (isUserFree) {
      dispatch(modalActions.showModal(PURCHASE_SUCCESS_MODAL));
    }
  }
};
